var _HiddenIcon, _VisibleIcon;
function _extends() { _extends = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }
import React, { useCallback, useEffect, useState } from 'react';
import { arrayOf, bool, func, node, number, object, oneOfType, shape, string } from 'prop-types';
import clsx from 'clsx';
import { Visibility as VisibleIcon, VisibilityOff as HiddenIcon } from 'mosaic-react-icons';
import SovosEditableDrawer from '../sovos-editable-drawer/SovosEditableDrawer';
import SovosOrderableList from '../sovos-orderable-list';
import useMosaicTranslation from '../internals/i18n/useMosaicTranslation';

/**
 * This Column Drawer offers toggling of the visibility of the columns of a
 * SovosTable
 */
const SovosColumnDrawer = _ref => {
  let {
    ApplyButtonProps,
    CancelButtonProps,
    className,
    columns,
    open,
    style,
    title,
    OrderableListProps,
    ...rest
  } = _ref;
  const {
    t
  } = useMosaicTranslation();
  const getColumns = useCallback(visible => columns.filter(col => {
    if (col.title == null || col.title === '') return false;
    return visible ? col.visible : !col.visible;
  }), [columns]);
  const [visibleColumns, setVisibleColumns] = useState(getColumns(true));
  const [hiddenColumns, setHiddenColumns] = useState(getColumns(false));
  const resetColumns = useCallback(() => {
    setVisibleColumns(getColumns(true));
    setHiddenColumns(getColumns(false));
  }, [getColumns]);
  useEffect(() => {
    resetColumns();
  }, [getColumns, resetColumns, columns]);
  const onChange = (visible, hidden) => {
    setVisibleColumns(visible.map(col => ({
      ...col,
      visible: true
    })));
    setHiddenColumns(hidden.map(col => ({
      ...col,
      visible: false
    })));
  };
  const onApply = () => {
    ApplyButtonProps.onClick([...visibleColumns, ...hiddenColumns]);
  };
  const onCancel = () => {
    CancelButtonProps.onClick();
    resetColumns();
  };
  return /*#__PURE__*/React.createElement(SovosEditableDrawer, _extends({
    className: clsx('sovosColumnDrawer', className),
    onClose: onCancel,
    open: open,
    PrimaryButtonProps: {
      children: t('apply'),
      className: 'sovosColumnDrawer__applyButton',
      ...ApplyButtonProps,
      onClick: onApply
    },
    SecondaryButtonProps: {
      children: t('cancel'),
      className: 'sovosColumnDrawer__cancelButton',
      ...CancelButtonProps,
      onClick: onCancel
    },
    style: style,
    title: title || t('columnDrawer.title')
  }, rest), /*#__PURE__*/React.createElement(SovosOrderableList, {
    activeIcon: _HiddenIcon || (_HiddenIcon = /*#__PURE__*/React.createElement(HiddenIcon, null)),
    activeItems: visibleColumns,
    inactiveIcon: _VisibleIcon || (_VisibleIcon = /*#__PURE__*/React.createElement(VisibleIcon, null)),
    inactiveItems: hiddenColumns,
    labels: {
      addTooltipText: (OrderableListProps === null || OrderableListProps === void 0 ? void 0 : OrderableListProps.addTooltipText) || t('columnDrawer.addTooltipText'),
      header: (OrderableListProps === null || OrderableListProps === void 0 ? void 0 : OrderableListProps.header) || t('columnDrawer.header'),
      removeTooltipText: (OrderableListProps === null || OrderableListProps === void 0 ? void 0 : OrderableListProps.removeTooltipText) || t('columnDrawer.removeTooltipText'),
      removeDisabledTooltipText: (OrderableListProps === null || OrderableListProps === void 0 ? void 0 : OrderableListProps.removeDisabledTooltipText) || t('columnDrawer.removeDisabledTooltipText')
    },
    minActiveItems: 1,
    onChange: onChange
  }));
};
SovosColumnDrawer.propTypes = {
  /**
   * Props for the apply button with required `onClick` callback method
   * that fires on button click. Default children is 'Apply'
   */
  ApplyButtonProps: shape({
    children: string,
    onClick: func.isRequired
  }).isRequired,
  /**
   * Props for the cancel button with required `onClick` callback method
   * that fires on button click. Default children is 'Cancel'
   */
  CancelButtonProps: shape({
    children: string,
    onClick: func.isRequired
  }).isRequired,
  /**
   * Extend the class name applied to the root element
   */
  className: string,
  /**
   * Array of objects that organize the order and visibility of table columns
   */
  columns: arrayOf(shape({
    id: oneOfType([string, number]).isRequired,
    title: string.isRequired,
    visible: bool
  })).isRequired,
  /**
   * If true, the column drawer is open.
   */
  open: bool.isRequired,
  /**
   * Props applied to the orderable list element
   */
  OrderableListProps: shape({
    labels: object
  }),
  /**
   * Inline styles applied to the root element
   */
  style: object,
  /**
   * Title of the column drawer
   */
  title: node
};
export default SovosColumnDrawer;